/* global t, runtime, mergeState, safeArray, num, buildLedgerLines, ledgerLineAgeBucket, bankReconMonthRange, getReportsModel, deriveReports */
// ØVRIG BALANSE — lesemodellen bak restkontoene
//
// Området er alt som ikke avstemmes et annet sted: forskuddsbetalte kostnader,
// forskudd til leverandører, andre kortsiktige fordringer, mellomværender,
// påløpte poster. De har ingen ekstern saldo og ingen reskontro — ingen bank
// bekrefter dem, og ingen kundeliste spesifiserer dem.
//
// Derfor er avstemmingen her SPESIFIKASJONEN: hva består saldoen av, post for
// post, og hvor gammel er hver post. Postene finnes ikke som et eget register;
// de er hovedbokslinjene på kontoen, gruppert på det de gjelder og nettet mot
// hverandre. En gruppe som går i null er gjort opp; en gruppe som står igjen er
// en åpen post.
//
// Linjene kommer fra `buildLedgerLines`, som er det samme utvalget SAF-T og
// komplett regnskapseksport leser. Ingen egen innsamler: to steder å bestemme
// hva som er bokført ville gitt to svar, og avstemmingen ville avstemt mot seg
// selv.
//
// Modellen er READ-ONLY og har ingen mockdata. Har foretaket ingen posteringer
// på kontoene, er lista tom og flaten sier det.

const OVRIG_TOLERANCE = 0.005;
const ovrigRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

// Aldersintervallene er de samme hovedboken bruker ellers i avstemmingen. Et
// eget sett her ville betydd at «over 90 dager» var to forskjellige ting
// avhengig av hvilken skjerm man sto på.
// The Norwegian strings are ids (produced by ledgerLineAgeBucket and compared in
// logic); only the label shown to the user is translated, by ovrigAgeLabel.
const OVRIG_AGE_BUCKETS = ['0–30 dager', '31–60 dager', '61–90 dager', 'Over 90 dager', 'Uten dato'];
const ovrigAgeLabel = (bucket) => {
  switch (bucket) {
    case '0–30 dager': return t('rco.age.d0to30', '0–30 dager');
    case '31–60 dager': return t('rco.age.d31to60', '31–60 dager');
    case '61–90 dager': return t('rco.age.d61to90', '61–90 dager');
    case 'Over 90 dager': return t('rco.age.over90', 'Over 90 dager');
    case 'Uten dato': return t('rco.age.noDate', 'Uten dato');
    default: return bucket;
  }
};

// Hva posten GJELDER, ikke hvilket bilag den kom fra. Det er teksten som binder
// forskuddsbetalingen i juli til kostnadsføringen i august — to bilag, én post.
// Uten grupperingen ville hver postering stått som sin egen åpne post, og en
// konto som er gjort opp ville sett ut som femten uavklarte krav.
const ovrigItemKey = (line) => {
  const description = String(line?.description || '').trim().toLowerCase();
  if (description) return description;
  const voucherName = String(line?.voucherName || '').trim().toLowerCase();
  if (voucherName) return voucherName;
  return String(line?.voucherNumber || line?.sourceId || 'uten tekst').trim().toLowerCase();
};

const ovrigItemLabel = (line) => String(line?.description || line?.voucherName || '').trim() || t('rco.noText', 'Uten tekst');

// Eiendel eller gjeld. Klassifiseringen tas fra saldobalansen appen allerede
// regner ut — den gjettes ikke av kontonummeret. En importert kontoplan og en
// svensk eller dansk kontoplan følger ikke den norske 1xxx/2xxx-regelen, og en
// oppfunnet regel her ville vært feil for begge.
const ovrigAccountGroups = (periodId, state) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  const groups = new Map();
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return groups;
  const options = { year, months: [monthIndex, monthIndex], includeChart: false, includeResult: false };
  let reports = null;
  try {
    // Den bufrede modellen når appen kjører; den rene utledningen når en
    // tilstand sendes inn. Begge er det SAMME regnestykket — saldobalansen
    // avstemmingen ellers leser bokført saldo fra.
    reports = state && typeof deriveReports === 'function'
      ? deriveReports(state, year, options)
      : (typeof getReportsModel === 'function' ? getReportsModel(options) : null);
  } catch (error) {
    return groups;
  }
  for (const group of safeArray(reports?.trial)) {
    for (const account of safeArray(group.accounts)) {
      const code = String(account?.nr || '').trim();
      if (code) groups.set(code, group.key === 'eiendeler' ? 'eiendel' : 'gjeld');
    }
  }
  return groups;
};

// Varslene en åpen post kan bære. De er OBSERVASJONER, ikke konklusjoner: en
// rund sum er ikke feil, en post uten bevegelse er ikke uten videre gammel
// søppel. De peker på hva den som avstemmer skal se nærmere på — vurderingen er
// hens, og den lagres på posten.
const ovrigItemFlags = ({ item, account, range }) => {
  const flags = [];
  if (item.ageBucket === 'Over 90 dager') flags.push('gammel');
  if (!String(item.description || '').trim() || /^ukjent/i.test(String(item.description || ''))) {
    flags.push('motpart_mangler');
  }
  // Et fortegn som peker motsatt vei av kontoens egen saldo er enten en
  // feilføring eller en post som hører hjemme på en annen konto. Begge deler
  // skal ses på.
  const accountSign = Math.sign(ovrigRound(account?.bookedBalance));
  if (accountSign !== 0 && Math.sign(item.amount) !== 0 && Math.sign(item.amount) !== accountSign) {
    flags.push('uvanlig_fortegn');
  }
  if (Math.abs(item.amount) >= 1000 && Math.abs(item.amount) % 1000 === 0) flags.push('rundt_belop');
  // Ingen bevegelse i perioden betyr at posten er med fra tidligere. Den er
  // ikke feil av den grunn — men den er heller ikke sett på denne måneden.
  if (range && item.lastDate < range.from) flags.push('ingen_bevegelse');
  // En manuell postering datert i de siste dagene av perioden er den klassiske
  // periodeavslutningsposten. Den skal kunne forklares av noe annet enn at
  // saldoen skulle bli riktig.
  if (item.manualAtPeriodEnd) flags.push('manuell_periodeslutt');
  return flags;
};

// En manuell postering er en som ikke kom fra en faktura, en betaling eller en
// lønnskjøring — den er ført for hånd. Kildetypen er bilagets egen; den utledes
// ikke av beløpet eller av kontoen.
// Er posten vesentlig? Ren aritmetikk mot grensen byrået selv har satt. Speiler
// `isMaterialAmount` i backend — regelen eies der, denne finnes fordi flaten
// ikke kan importere fra functions.
//
// `null` betyr «ingen grense å måle mot», ikke «uvesentlig». Behandlet de to
// likt, ville en klient uten grense fått alle poster stemplet som småpenger.
const ovrigIsMaterial = (amount, threshold) => (threshold === null || threshold === undefined
  ? null
  : Math.abs(num(amount)) >= num(threshold));

const ovrigIsManual = (line) => /yearend|manual|journal|adjust|årsoppgjør|arsoppgjor/i
  .test(String(line?.sourceType || '') + ' ' + String(line?.kind || ''));

// Postene på én konto: hovedbokslinjene fra regnskapets begynnelse til og med
// periodeslutt, gruppert på det de gjelder og nettet. Grupper som går i null er
// gjort opp og telles for seg — de er ikke åpne poster, men de er heller ikke
// ingenting: at en konto har femten oppgjorte poster og to åpne er nettopp det
// spesifikasjonen skal vise.
const ovrigBuildItems = ({ lines, accounts, range, materialityThreshold = null }) => {
  const accountById = new Map(safeArray(accounts).map((account) => [String(account.accountId), account]));
  const groups = new Map();
  for (const line of safeArray(lines)) {
    const key = String(line.accountCode) + '::' + ovrigItemKey(line);
    if (!groups.has(key)) {
      groups.set(key, {
        id: key,
        accountId: String(line.accountCode),
        accountName: line.accountName || (accountById.get(String(line.accountCode)) || {}).accountName || '',
        description: ovrigItemLabel(line),
        amount: 0,
        lines: [],
        firstDate: line.date,
        lastDate: line.date,
        manualAtPeriodEnd: false,
      });
    }
    const group = groups.get(key);
    group.amount = ovrigRound(group.amount + num(line.amount));
    group.lines.push(line);
    if (line.date < group.firstDate) group.firstDate = line.date;
    if (line.date > group.lastDate) group.lastDate = line.date;
    if (range && ovrigIsManual(line) && line.date >= range.from && line.date >= ovrigLastDays(range.to)) {
      group.manualAtPeriodEnd = true;
    }
  }

  const open = [];
  let settledCount = 0;
  for (const group of groups.values()) {
    if (Math.abs(group.amount) <= OVRIG_TOLERANCE) { settledCount += 1; continue; }
    const item = {
      ...group,
      lineCount: group.lines.length,
      ageBucket: ledgerLineAgeBucket(group.firstDate, range?.to),
      ageDays: ovrigAgeDays(group.firstDate, range?.to),
    };
    item.material = ovrigIsMaterial(item.amount, materialityThreshold);
    item.flags = ovrigItemFlags({ item, account: accountById.get(item.accountId), range });
    open.push(item);
  }
  open.sort((left, right) => String(left.firstDate).localeCompare(String(right.firstDate))
    || Math.abs(right.amount) - Math.abs(left.amount));
  return { items: open, settledCount };
};

const ovrigLastDays = (toIso) => {
  const date = new Date(String(toIso) + 'T00:00:00');
  if (Number.isNaN(date.getTime())) return String(toIso);
  date.setDate(date.getDate() - 4);
  return date.toISOString().slice(0, 10);
};

const ovrigAgeDays = (fromIso, toIso) => {
  const from = new Date(String(fromIso) + 'T00:00:00');
  const to = new Date(String(toIso) + 'T00:00:00');
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return Math.round((to - from) / 86400000);
};

// Kontoraden: bokført saldo fra saldobalansen, og summen av de åpne postene
// ved siden av. Sprikker de to, er saldoen ikke spesifisert — og det er den
// ene kontrollen dette området faktisk kan gjøre uten et eksternt grunnlag.
const ovrigBuildAccounts = ({ accounts, items, groupsByAccount }) => safeArray(accounts).map((account) => {
  const code = String(account.accountId);
  const rows = items.filter((item) => item.accountId === code);
  const specified = ovrigRound(rows.reduce((sum, item) => sum + item.amount, 0));
  const booked = ovrigRound(account.bookedBalance);
  const oldest = rows.reduce((value, item) => (!value || item.firstDate < value ? item.firstDate : value), null);
  return {
    accountId: code,
    accountName: account.accountName || '',
    // `type` is the stable id compared in logic; `typeLabel` is what the UI shows.
    type: groupsByAccount.get(code) === 'gjeld' ? 'Gjeld' : 'Eiendel',
    typeLabel: groupsByAccount.get(code) === 'gjeld' ? t('rco.type.liability', 'Gjeld') : t('rco.type.asset', 'Eiendel'),
    bookedBalance: booked,
    openingBalance: ovrigRound(account.openingBalance),
    movement: ovrigRound(account.movement),
    specifiedAmount: specified,
    specificationDifference: ovrigRound(booked - specified),
    itemCount: rows.length,
    oldItemCount: rows.filter((item) => item.ageBucket === 'Over 90 dager').length,
    oldestItemDate: oldest,
    flaggedCount: rows.filter((item) => item.flags.length).length,
  };
});

const buildOtherBalanceReconciliationView = ({
  periodId, accounts = [], materialityThreshold = null, locked = false, state: suppliedState, today: suppliedToday,
} = {}) => {
  const state = suppliedState || mergeState(runtime.appState);
  if (window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state)) {
    return {
      ...window.PlummeAccountingBasisCoverage.blockedReadModel('other_balance_reconciliation', state.accountingBasisCoverage),
      periodId,
      accounts: null,
      items: null,
      aging: null,
      ledgerLines: null,
      counts: null,
      summary: null,
      checks: null,
    };
  }
  const range = bankReconMonthRange(periodId);
  const empty = {
    periodId, from: '', to: '', accounts: [], items: [], aging: [], ledgerLines: [],
    counts: {}, summary: {}, checks: [], settledCount: 0, accountsEmpty: true,
    materialityThreshold: null, preliminary: false, periodEnded: true, dataThrough: '',
  };
  if (!range) return empty;
  // En avstemming av en periode som ikke er over er en FORELØPIG avstemming: de
  // resterende dagenes bilag finnes ikke ennå, og saldoen kommer til å flytte
  // seg. Det samme gjelder en periode som er over men ikke låst — bilag kan
  // fortsatt bokføres. Begge deler skal stå i flaten og ikke i en fotnote: det
  // avgjør hvor sterkt avstemmingen kan konkluderes.
  const today = String(suppliedToday || new Date().toISOString().slice(0, 10));
  const periodEnded = today > range.to;
  const dataThrough = periodEnded ? range.to : today;
  const threshold = materialityThreshold === null || materialityThreshold === undefined
    || !Number.isFinite(Number(materialityThreshold))
    ? null
    : Number(materialityThreshold);
  const codes = safeArray(accounts).map((account) => String(account.accountId).trim()).filter(Boolean);
  if (!codes.length) {
    return {
      ...empty, from: range.from, to: range.to,
      materialityThreshold: threshold, preliminary: !periodEnded || locked !== true, periodEnded, dataThrough,
    };
  }

  // Fra regnskapets begynnelse til og med periodeslutt. En balansekonto bærer
  // med seg alt som ikke er gjort opp — leser vi bare perioden, ser en fordring
  // fra april ut som om den ikke finnes.
  const allLines = buildLedgerLines({ to: range.to, accountCodes: codes, state });
  const periodLines = allLines.filter((line) => line.date >= range.from);
  const groupsByAccount = ovrigAccountGroups(periodId, state);
  const { items, settledCount } = ovrigBuildItems({ lines: allLines, accounts, range, materialityThreshold: threshold });
  const accountRows = ovrigBuildAccounts({ accounts, items, groupsByAccount });

  const sum = (rows, pick) => ovrigRound(rows.reduce((total, row) => total + num(pick(row)), 0));
  const assetRows = accountRows.filter((row) => row.type === 'Eiendel');
  const liabilityRows = accountRows.filter((row) => row.type === 'Gjeld');
  const bookedTotal = sum(accountRows, (row) => row.bookedBalance);
  const specifiedTotal = sum(items, (item) => item.amount);

  const aging = OVRIG_AGE_BUCKETS.map((bucket) => {
    const rows = items.filter((item) => item.ageBucket === bucket);
    return { key: bucket, label: ovrigAgeLabel(bucket), count: rows.length, amount: sum(rows, (row) => row.amount) };
  }).filter((bucket) => bucket.count > 0);

  const has = (flag) => items.filter((item) => item.flags.includes(flag)).length;
  const counts = {
    accounts: accountRows.length,
    items: items.length,
    settled: settledCount,
    ledgerLines: periodLines.length,
    gammel: has('gammel'),
    motpartMangler: has('motpart_mangler'),
    uvanligFortegn: has('uvanlig_fortegn'),
    rundtBelop: has('rundt_belop'),
    ingenBevegelse: has('ingen_bevegelse'),
    manuellPeriodeslutt: has('manuell_periodeslutt'),
    flagged: items.filter((item) => item.flags.length).length,
    material: items.filter((item) => item.material === true).length,
    immaterial: items.filter((item) => item.material === false).length,
    specificationDeviations: accountRows.filter((row) => Math.abs(row.specificationDifference) > OVRIG_TOLERANCE).length,
  };

  // Den automatiske kontrollen. Den sier at spesifikasjonen summerer seg til
  // hovedboken — ikke at postene er reelle, dokumenterte eller gjenvinnbare.
  // Skillet står i flaten fordi det er hele forskjellen på en teknisk kontroll
  // og en faglig avstemming.
  const checks = [
    {
      key: 'spesifikasjon',
      label: t('rco.check.specVsLedger', 'Spesifiserte poster mot hovedboken'),
      value: counts.specificationDeviations,
      note: counts.specificationDeviations ? t('rco.check.notFullySpecified', 'Saldoen er ikke fullt spesifisert') : t('rco.check.sumsToLedger', 'Summerer seg til hovedboken'),
    },
    { key: 'gammel', label: t('rco.check.older90', 'Poster eldre enn 90 dager'), value: counts.gammel },
    { key: 'motpart_mangler', label: t('rco.check.noCounterparty', 'Poster uten identifisert motpart'), value: counts.motpartMangler },
    { key: 'uvanlig_fortegn', label: t('rco.check.unusualSign', 'Poster med uvanlig fortegn'), value: counts.uvanligFortegn },
    { key: 'ingen_bevegelse', label: t('rco.check.noMovement', 'Poster uten bevegelse i perioden'), value: counts.ingenBevegelse },
    { key: 'manuell_periodeslutt', label: t('rco.check.manualAtPeriodEnd', 'Manuelle posteringer ved periodeslutt'), value: counts.manuellPeriodeslutt },
    { key: 'rundt_belop', label: t('rco.check.roundAmounts', 'Runde beløp'), value: counts.rundtBelop },
  ];

  return {
    periodId,
    from: range.from,
    to: range.to,
    today,
    periodEnded,
    dataThrough,
    // Foreløpig så lenge perioden ikke er over ELLER ikke er låst. Begge deler
    // betyr at grunnlaget fortsatt kan flytte seg.
    preliminary: !periodEnded || locked !== true,
    materialityThreshold: threshold,
    accounts: accountRows,
    items,
    aging,
    ledgerLines: periodLines,
    counts,
    checks,
    settledCount,
    accountsEmpty: accountRows.length === 0,
    summary: {
      bookedTotal,
      assetTotal: sum(assetRows, (row) => row.bookedBalance),
      liabilityTotal: sum(liabilityRows, (row) => row.bookedBalance),
      openingTotal: sum(accountRows, (row) => row.openingBalance),
      movementTotal: sum(accountRows, (row) => row.movement),
      specifiedTotal,
      specificationDifference: ovrigRound(bookedTotal - specifiedTotal),
      oldTotal: sum(items.filter((item) => item.ageBucket === 'Over 90 dager'), (item) => item.amount),
      oldCount: counts.gammel,
      materialTotal: sum(items.filter((item) => item.material === true), (item) => item.amount),
      immaterialTotal: sum(items.filter((item) => item.material === false), (item) => item.amount),
    },
  };
};

Object.assign(window, {
  buildOtherBalanceReconciliationView,
  ovrigIsMaterial, ovrigBuildItems, ovrigBuildAccounts, ovrigItemKey, ovrigItemFlags, ovrigAccountGroups,
  OVRIG_AGE_BUCKETS, OVRIG_TOLERANCE, ovrigAgeLabel,
});
